// src/components/AdminPage.js
import React, { useEffect, useState } from 'react';
import {
  collection, onSnapshot, addDoc, deleteDoc, doc,
  serverTimestamp, updateDoc, query, where, orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const ADMIN_EMAIL = 'urano@hgu.jp';

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab,      setTab]      = useState('requests'); // 'requests' | 'allowed' | 'events'
  const [allowed,  setAllowed]  = useState([]);
  const [requests, setRequests] = useState([]);
  const [events,   setEvents]   = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newName,  setNewName]  = useState('');
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    if (!isAdmin) return;
    const unsub1 = onSnapshot(collection(db, 'allowedCreators'), snap => {
      setAllowed(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsub2 = onSnapshot(
      query(collection(db, 'creatorRequests'), where('status', '==', 'pending')),
      snap => { setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() }))); }
    );
    const unsub3 = onSnapshot(
      query(collection(db, 'events'), orderBy('createdAt', 'desc')),
      snap => { setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() }))); }
    );
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [isAdmin]);

  if (!user) {
    return (
      <div className="page"><div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <p>ログインが必要です</p>
        </div>
      </div></div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="page"><div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <p className="text-muted">管理者権限がありません</p>
        </div>
      </div></div>
    );
  }

  async function approveRequest(req) {
    setError(''); setSuccess('');
    try {
      await addDoc(collection(db, 'allowedCreators'), {
        email:   req.email,
        name:    req.name,
        addedBy: user.email,
        addedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'creatorRequests', req.id), {
        status: 'approved', resolvedAt: serverTimestamp(),
      });
      setSuccess(`${req.name}（${req.email}）を承認しました`);
    } catch (e) {
      setError('承認に失敗しました');
    }
  }

  async function rejectRequest(req) {
    if (!window.confirm(`${req.name} の申請を却下しますか？`)) return;
    await updateDoc(doc(db, 'creatorRequests', req.id), {
      status: 'rejected', resolvedAt: serverTimestamp(),
    });
  }

  async function addAllowed() {
    setError(''); setSuccess('');
    const email = newEmail.trim().toLowerCase();
    if (!email.includes('@')) { setError('正しいメールアドレスを入力してください'); return; }
    if (allowed.find(a => a.email === email)) { setError('すでに登録済みです'); return; }
    try {
      await addDoc(collection(db, 'allowedCreators'), {
        email, name: newName.trim(), addedBy: user.email, addedAt: serverTimestamp(),
      });
      setNewEmail(''); setNewName('');
      setSuccess(`${email} を追加しました`);
    } catch (e) {
      setError('追加に失敗しました');
    }
  }

  async function removeAllowed(id, email) {
    if (!window.confirm(`${email} の作成権限を削除しますか？`)) return;
    await deleteDoc(doc(db, 'allowedCreators', id));
  }

  async function deleteEvent(ev) {
    if (!window.confirm(`「${ev.title}」を削除しますか？この操作は取り消せません。`)) return;
    await deleteDoc(doc(db, 'events', ev.id));
    setSuccess(`「${ev.title}」を削除しました`);
  }

  async function closeEvent(ev) {
    if (!window.confirm(`「${ev.title}」を締め切りますか？`)) return;
    await updateDoc(doc(db, 'events', ev.id), { closedAt: serverTimestamp() });
    setSuccess(`「${ev.title}」を締め切りました`);
  }

  const TABS = [
    { key: 'requests', label: `申請${requests.length > 0 ? ` (${requests.length})` : ''}` },
    { key: 'allowed',  label: `許可アカウント (${allowed.length})` },
    { key: 'events',   label: `全イベント (${events.length})` },
  ];

  return (
    <div className="page">
      <div className="container">
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: '1rem' }}>管理画面</h1>

        {/* タブ */}
        <div style={{ display: 'flex', marginBottom: '1rem', background: '#fff', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: 10, overflow: 'hidden' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setError(''); setSuccess(''); }}
              style={{
                flex: 1, padding: '10px', fontSize: 13, border: 'none', cursor: 'pointer',
                background: tab === t.key ? '#1D9E75' : '#fff',
                color: tab === t.key ? '#fff' : '#555',
                fontWeight: tab === t.key ? 500 : 400,
              }}>
              {t.label}
              {t.key === 'requests' && requests.length > 0 && (
                <span style={{
                  display: 'inline-block', marginLeft: 4,
                  background: '#E24B4A', color: '#fff',
                  borderRadius: 20, fontSize: 10, padding: '1px 5px', fontWeight: 600
                }}>{requests.length}</span>
              )}
            </button>
          ))}
        </div>

        {error   && <div className="msg-error mb-2">{error}</div>}
        {success && <div className="msg-success mb-2">{success}</div>}

        {/* 申請タブ */}
        {tab === 'requests' && (
          <div className="card">
            <div className="card-title">作成権限の申請</div>
            {requests.length === 0 ? (
              <p className="text-sm text-muted">新しい申請はありません</p>
            ) : requests.map(req => (
              <div key={req.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 0', borderBottom: '0.5px solid rgba(0,0,0,0.06)', gap: 8, flexWrap: 'wrap'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {req.photoURL
                    ? <img src={req.photoURL} alt="" className="avatar" referrerPolicy="no-referrer" />
                    : <div className="avatar-initials">{(req.name || '?')[0]}</div>
                  }
                  <div>
                    <div className="font-medium text-sm">{req.name}</div>
                    <div className="text-sm text-muted">{req.email}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => approveRequest(req)}>承認</button>
                  <button className="btn btn-sm btn-danger" onClick={() => rejectRequest(req)}>却下</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 許可アカウントタブ */}
        {tab === 'allowed' && (
          <>
            <div className="card">
              <div className="card-title">直接追加</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <input type="text" placeholder="メールアドレス" value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addAllowed()}
                  style={{ flex: 2, minWidth: 200 }} />
                <input type="text" placeholder="氏名（メモ用）" value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addAllowed()}
                  style={{ flex: 1, minWidth: 120 }} />
                <button className="btn btn-primary" onClick={addAllowed}>追加</button>
              </div>
              <p className="text-sm text-muted">※ 管理者（{ADMIN_EMAIL}）は常に作成可能です</p>
            </div>
            <div className="card">
              <div className="card-title">許可済みアカウント（{allowed.length}件）</div>
              {allowed.length === 0 ? (
                <p className="text-sm text-muted">まだ追加されていません</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: '0.5px solid rgba(0,0,0,0.1)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, fontSize: 12, color: '#888' }}>メールアドレス</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, fontSize: 12, color: '#888' }}>氏名</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, fontSize: 12, color: '#888' }}>追加日</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {allowed.map(a => (
                      <tr key={a.id} style={{ borderBottom: '0.5px solid rgba(0,0,0,0.06)' }}>
                        <td style={{ padding: '8px 8px' }}>{a.email}</td>
                        <td style={{ padding: '8px 8px', color: '#666' }}>{a.name || '—'}</td>
                        <td style={{ padding: '8px 8px', color: '#999', fontSize: 13 }}>
                          {a.addedAt?.toDate?.()?.toLocaleDateString('ja-JP') || '—'}
                        </td>
                        <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                          <button className="btn btn-sm btn-danger" onClick={() => removeAllowed(a.id, a.email)}>削除</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* 全イベントタブ */}
        {tab === 'events' && (
          <div className="card">
            <div className="card-title">全イベント一覧</div>
            {events.length === 0 ? (
              <p className="text-sm text-muted">イベントがありません</p>
            ) : events.map(ev => (
              <div key={ev.id} style={{
                padding: '12px 0', borderBottom: '0.5px solid rgba(0,0,0,0.06)'
              }}>
                <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Link to={`/event/${ev.id}`} style={{ fontWeight: 500, color: '#1D9E75' }}>
                        {ev.title}
                      </Link>
                      {ev.closedAt
                        ? <span className="badge" style={{ background: '#FCEBEB', color: '#791F1F', fontSize: 11 }}>締め切り済み</span>
                        : <span className="badge badge-ok" style={{ fontSize: 11 }}>受付中</span>
                      }
                    </div>
                    <div className="text-sm text-muted" style={{ marginTop: 4 }}>
                      主催：{ev.hostName}（{ev.hostEmail}）
                      　候補日 {ev.slots?.length || 0}件
                      {ev.createdAt && `　作成：${ev.createdAt.toDate?.()?.toLocaleDateString('ja-JP')}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {!ev.closedAt && (
                      <button className="btn btn-sm" onClick={() => closeEvent(ev)}>締め切る</button>
                    )}
                    <button className="btn btn-sm btn-danger" onClick={() => deleteEvent(ev)}>削除</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
